import type { StringFacet } from '@shared/gql/document-nodes'
import type { FC } from 'react'

import { FacetKey } from '@shared/gql/document-nodes'
import { CheckboxGroup } from 'components/common/CheckboxGroup/CheckboxGroup'
import { NO_BRAND_BRANDNAME } from 'domain/product'
import { difference } from 'ramda'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useActiveFilters } from 'services/Search/hooks/use-active-filters'
import { facetKeyToFilter } from 'services/Search/utils/filters'
import { useURLSearchParams } from 'utils/hooks/use-url-search-params'

interface Props {
  className?: string
  facet: StringFacet
}

export const StringFilter: FC<Props> = ({ facet }) => {
  const { t } = useTranslation()
  const { addParams, removeParams } = useURLSearchParams()
  const activeFilters = useActiveFilters()

  const activeFilterValues = useMemo(
    () =>
      activeFilters
        .find((activeFilter) => activeFilter.key === facet.key)
        ?.stringValue.map((v) => v) || [],
    [activeFilters, facet.key],
  )

  const updateFilter = useCallback(
    (newValue: string[]) => {
      const toAdd = difference(newValue, activeFilterValues)
      const toRemove = difference(activeFilterValues, newValue)
      if (toAdd.length > 0) {
        const params: [string, string][] = toAdd.map((x) => [facetKeyToFilter(facet.key), x])
        addParams(...params)
      }
      if (toRemove.length > 0) {
        const params: [string, string][] = toRemove.map((x) => [facetKeyToFilter(facet.key), x])
        removeParams(...params)
      }
    },
    [facet.key, activeFilterValues, addParams, removeParams],
  )

  const options = useMemo(
    () =>
      facet.stringValue
        .filter(
          /** @todo [VOIK-8881] this filter is not needed after "Ei merkkiä" is nulled in the backend */
          (value) => facet.key !== FacetKey.BrandName || value !== NO_BRAND_BRANDNAME,
        )
        .map((value) => ({ label: t(value), value })),
    [facet, t],
  )

  return (
    <CheckboxGroup
      name={`search-filter-checkbox-${facet.key}`}
      options={options}
      onChange={updateFilter}
      value={activeFilterValues}
    />
  )
}
